import React, { useState } from 'react';
import { AlertTriangle, X, Loader2 } from 'lucide-react';

const ConfirmDialog = ({
  open, title, message, warning, confirmText = 'Confirmar', cancelText = 'Cancelar',
  confirmWord, tone = 'danger', onClose, onConfirm,
}) => {
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const needsWord = !!confirmWord;
  const canConfirm = !needsWord || input.trim() === confirmWord;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      setInput('');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setInput('');
    setError(null);
    onClose();
  };

  const toneCls = tone === 'danger'
    ? { bg: 'bg-danger/10', fg: 'text-danger', btn: 'bg-danger hover:bg-red-700' }
    : { bg: 'bg-primary-soft', fg: 'text-primary-darker', btn: 'bg-primary hover:bg-primary-dark' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${toneCls.bg} ${toneCls.fg}`}>
              <AlertTriangle size={18} />
            </div>
            <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
          </div>
          <button onClick={handleClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {message && <p className="text-sm text-ink">{message}</p>}
          {warning && (
            <div className="border border-warning/30 bg-warning/5 rounded-lg p-3 text-xs text-ink">
              <div className="font-bold text-warning uppercase tracking-wider mb-1">Atención</div>
              {warning}
            </div>
          )}
          {needsWord && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
                Escribe <span className="font-mono text-ink">{confirmWord}</span> para confirmar
              </label>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                autoFocus
              />
            </div>
          )}
          {error && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">
              {error.message || 'Error al confirmar.'}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 ${toneCls.btn}`}
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
